import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ListFilter } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { documentGroupApi } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";

interface DocumentGroupFilterProps {
  values: number[];
  onChange: (values: number[]) => void;
}

export const DocumentGroupFilter = ({ values, onChange }: DocumentGroupFilterProps) => {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const { permissionsLoaded, selectedRole } = useAuth();
  
  const { data: documentGroups = [], isLoading } = useQuery({
    queryKey: ["document-groups", selectedRole],
    queryFn: () => {
      // Backend now handles role-based filtering automatically through permissions
      return documentGroupApi.list();
    },
    enabled: permissionsLoaded && !!selectedRole,
    meta: {
      onError: (error: Error) => {
        toast({
          title: "Error loading document groups",
          description: error.message,
          variant: "destructive"
        });
      }
    }
  });

  const checkboxOptions = [
    { id: "all", label: "All Groups" },
    ...documentGroups
      .filter(group => 
        search.trim() === "" || 
        group.name.toLowerCase().includes(search.toLowerCase())
      )
      .map(group => ({
        id: group.id,
        label: group.name
      }))
  ];

  const handleChange = (selectedValues: (string | number)[]) => {
    if (selectedValues.includes("all")) {
      onChange([]);
    } else {
      onChange(selectedValues.map(v => typeof v === 'string' ? parseInt(v) : v));
    }
  };

  const handleItemClick = (id: string | number) => {
    const isAll = id === "all";
    const isCurrentlySelected = values.length === 0 
      ? isAll 
      : (typeof id === 'number' && values.includes(id));
    
    let newValues: (string | number)[];
    
    if (isAll) {
      newValues = isCurrentlySelected ? [] : ["all"];
    } else {
      if (isCurrentlySelected) {
        newValues = values.filter(v => v !== id);
        if (newValues.length === 0) newValues = ["all"];
      } else {
        // Ensure we're not adding "all" to the list of values and only working with numbers
        newValues = [...values.filter(v => typeof v === 'number'), id];
      }
    }
    
    handleChange(newValues);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-[200px] justify-start"
        >
          <ListFilter className="mr-2 h-4 w-4" />
          {values.length === 0 ? (
            "All Groups"
          ) : (
            <div className="flex items-center gap-1">
              <span>Groups:</span>
              <Badge variant="secondary">{values.length}</Badge>
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-4" align="start">
        {isLoading? <div className="space-y-4">Loading...</div> : <div className="space-y-4">
          <Input
            type="search"
            placeholder="Search groups..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-2"
          />
          <ScrollArea className="h-[200px]">
            <div className="space-y-2">
              {checkboxOptions.map((option) => (
                <div 
                  key={option.id} 
                  className="flex items-center space-x-2 cursor-pointer py-1"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleItemClick(option.id);
                  }}
                >
                  <input
                    type="checkbox"
                    id={`checkbox-${option.id}`}
                    className="h-4 w-4 rounded-sm border border-primary"
                    checked={values.length === 0 
                      ? option.id === "all" 
                      : typeof option.id === "number" && values.includes(option.id)}
                    onChange={() => {}} // Handled by parent div click
                  />
                  <label
                    htmlFor={`checkbox-${option.id}`}
                    className="text-sm font-medium leading-none select-none"
                  >
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>}
      </PopoverContent>
    </Popover>
  );
};
